import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Badge,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Divider,
  Paper
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  ArrowBack as ArrowBackIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon
} from '@mui/icons-material';

// Status map
const statusIcons = {
  'open': { color: '#4C85F0', label: 'Open' },
  'pending': { color: '#FF9800', label: 'Pending' },
  'closed': { color: '#66BB6A', label: 'Closed' },
  'urgent': { color: '#F44336', label: 'Urgent' }
};

const ConversationsList = ({
  tickets,
  activeTicket,
  onSelectTicket,
  searchQuery,
  onSearch,
  activeSection,
  theme,
  isMobile,
  onBackToNavigation
}) => {
  // Format time difference
  const formatTimeAgo = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInDays > 0) {
      return `${diffInDays}d ago`;
    } else if (diffInHours > 0) {
      return `${diffInHours}h ago`;
    } else if (diffInMinutes > 0) {
      return `${diffInMinutes}m ago`;
    } else {
      return 'just now';
    }
  };

  // Get section title
  const getSectionTitle = () => {
    switch (activeSection) {
      case 'dashboard': return 'Dashboard';
      case 'inbox': return 'Inbox';
      case 'mentions': return 'Mentions';
      case 'unassigned': return 'Unassigned';
      case 'closed': return 'Closed';
      default: return 'Conversations';
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: { xs: '100%', md: 340 },
        height: '100%',
        borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        background: 'rgba(26, 32, 44, 0.7)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              onClick={onBackToNavigation}
              sx={{ mr: 1 }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 'medium', flexGrow: 1 }}>
            {getSectionTitle()}
          </Typography>
          <IconButton size="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            <FilterIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Search */}
        <TextField
          fullWidth
          placeholder="Search conversations..."
          size="small"
          value={searchQuery}
          onChange={(e) => onSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
              </InputAdornment>
            ),
            sx: {
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              color: 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.1)'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)'
              }
            }
          }}
        />
      </Box>

      {/* Tickets counter */}
      <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="rgba(255,255,255,0.7)">
          {tickets.length === 0
            ? 'No tickets found'
            : `${tickets.length} ticket${tickets.length > 1 ? 's' : ''}`}
        </Typography>
        <Box>
          <Chip
            label="Sort: Newest"
            size="small"
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              color: 'rgba(255, 255, 255, 0.7)',
              borderRadius: '4px',
              height: 24,
              '& .MuiChip-label': {
                px: 1,
                fontSize: '0.75rem'
              }
            }}
          />
        </Box>
      </Box>

      {/* Tickets list */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List disablePadding>
          {tickets.map((ticket) => {
            const isActive = activeTicket === ticket.id;
            const status = statusIcons[ticket.status] || statusIcons.open;
            const lastMessage = ticket.messages[ticket.messages.length - 1];
            
            return (
              <React.Fragment key={ticket.id}>
                <ListItem
                  alignItems="flex-start"
                  button
                  selected={isActive}
                  onClick={() => onSelectTicket(ticket.id)}
                  sx={{
                    borderLeft: isActive 
                      ? `4px solid ${theme.palette.primary.main}` 
                      : '4px solid transparent',
                    backgroundColor: isActive 
                      ? 'rgba(76, 133, 240, 0.1)' 
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: isActive 
                        ? 'rgba(76, 133, 240, 0.15)' 
                        : 'rgba(255, 255, 255, 0.05)'
                    },
                    transition: 'background-color 0.2s ease',
                    px: 2,
                    py: 1.5
                  }}
                >
                  <ListItemAvatar>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      variant="dot"
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: status.color,
                          border: '2px solid rgba(26, 32, 44, 0.7)'
                        }
                      }}
                    >
                      <Avatar 
                        alt={ticket.customer.name} 
                        src={ticket.customer.avatar}
                        sx={{ width: 40, height: 40 }}
                      >
                        {ticket.customer.name.charAt(0)}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: 'white',
                            fontWeight: ticket.unread ? 'bold' : 'medium',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {ticket.subject}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'rgba(255, 255, 255, 0.5)',
                              fontSize: '0.7rem',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {formatTimeAgo(ticket.lastActivity)}
                          </Typography>
                          <IconButton 
                            size="small" 
                            sx={{ 
                              color: ticket.starred ? '#FFD700' : 'rgba(255, 255, 255, 0.3)',
                              marginLeft: '-8px',
                              '&:hover': {
                                color: ticket.starred ? '#FFC107' : 'rgba(255, 255, 255, 0.7)'
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Handle starring logic here
                            }}
                          >
                            {ticket.starred ? (
                              <StarIcon fontSize="small" />
                            ) : (
                              <StarBorderIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, mb: 1 }}>
                          <Chip
                            label={status.label}
                            size="small"
                            sx={{
                              backgroundColor: `${status.color}20`,
                              color: status.color,
                              borderRadius: '4px',
                              height: 20,
                              mr: 1,
                              '& .MuiChip-label': {
                                px: 1,
                                fontSize: '0.7rem'
                              }
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'rgba(255, 255, 255, 0.5)',
                              fontSize: '0.7rem'
                            }}
                          >
                            #{ticket.id}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: ticket.unread ? 'white' : 'rgba(255, 255, 255, 0.7)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {lastMessage ? (
                            <>
                              <Typography 
                                component="span" 
                                variant="body2" 
                                sx={{ 
                                  color: 'rgba(255, 255, 255, 0.5)',
                                  fontWeight: 'medium'
                                }}
                              >
                                {lastMessage.senderName === ticket.customer.name 
                                  ? ticket.customer.name.split(' ')[0]
                                  : lastMessage.senderName}:
                              </Typography>
                              {' '}
                              {lastMessage.content}
                            </>
                          ) : (
                            'No messages yet'
                          )}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                <Divider variant="inset" component="li" sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
              </React.Fragment>
            );
          })}

          {tickets.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="rgba(255,255,255,0.5)">
                No tickets found
              </Typography>
            </Box>
          )}
        </List>
      </Box>
    </Paper>
  );
};

export default ConversationsList;
